<template>
  <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
    <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
      <div class="text-gray-500 text-sm">Unique clip views <span class="ml-1 text-gray-400 cursor-help info-tip" :title="tooltips.unique_views" role="img" aria-label="Info">ⓘ</span></div>
      <div class="text-3xl font-semibold">{{ stats.unique_views || 0 }}</div>
    </div>
    <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
      <div class="text-gray-500 text-sm">Total clip views <span class="ml-1 text-gray-400 cursor-help info-tip" :title="tooltips.total_views" role="img" aria-label="Info">ⓘ</span></div>
      <div class="text-3xl font-semibold">{{ stats.total_views || 0 }}</div>
    </div>
    <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
      <div class="text-gray-500 text-sm">Total downloads <span class="ml-1 text-gray-400 cursor-help info-tip" :title="tooltips.total_downloads" role="img" aria-label="Info">ⓘ</span></div>
      <div class="text-3xl font-semibold">{{ stats.total_downloads || 0 }}</div>
    </div>
    <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
      <div class="text-gray-500 text-sm">Unique clips shared <span class="ml-1 text-gray-400 cursor-help info-tip" :title="tooltips.unique_clips_shared" role="img" aria-label="Info">ⓘ</span></div>
      <div class="text-3xl font-semibold">{{ stats.unique_clips_shared || 0 }}</div>
    </div>
    <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
      <div class="text-gray-500 text-sm">Total clip shares <span class="ml-1 text-gray-400 cursor-help info-tip" :title="tooltips.total_shares" role="img" aria-label="Info">ⓘ</span></div>
      <div class="text-3xl font-semibold">{{ stats.total_shares || 0 }}</div>
    </div>
    <div v-if="showSessionAndPageViews" class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
      <div class="text-gray-500 text-sm">Sessions <span class="ml-1 text-gray-400 cursor-help info-tip" :title="tooltips.sessions" role="img" aria-label="Info">ⓘ</span></div>
      <div class="text-3xl font-semibold">{{ stats.sessions || 0 }}</div>
    </div>
    <div v-if="showSessionAndPageViews" class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
      <div class="text-gray-500 text-sm">Page views <span class="ml-1 text-gray-400 cursor-help info-tip" :title="tooltips.page_views" role="img" aria-label="Info">ⓘ</span></div>
      <div class="text-3xl font-semibold">{{ stats.page_views || 0 }}</div>
    </div>
    <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
      <div class="text-gray-500 text-sm">Graduate Amplification Index <span class="ml-1 text-gray-400 cursor-help info-tip" :title="metricTooltip('graduate_amplification_index')" role="img" aria-label="Info">ⓘ</span></div>
      <div class="text-3xl font-semibold">{{ metricLabel('graduate_amplification_index') }}</div>
      <div v-if="showBenchmark && benchmarkLabel('graduate_amplification_index')" class="mt-2 text-xs font-medium text-gray-600">
        Benchmark (rolling 12 months): {{ benchmarkLabel('graduate_amplification_index') }}
      </div>
    </div>
    <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
      <div class="text-gray-500 text-sm">Additional Audience per Graduate <span class="ml-1 text-gray-400 cursor-help info-tip" :title="metricTooltip('additional_audience_per_graduate')" role="img" aria-label="Info">ⓘ</span></div>
      <div class="text-3xl font-semibold">{{ metricLabel('additional_audience_per_graduate') }}</div>
    </div>
    <div class="rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white border">
      <div class="text-gray-500 text-sm">Views Generated per Share <span class="ml-1 text-gray-400 cursor-help info-tip" :title="metricTooltip('observed_share_amplification')" role="img" aria-label="Info">ⓘ</span></div>
      <div class="text-3xl font-semibold">{{ metricLabel('observed_share_amplification') }}</div>
      <div
        v-if="stats.observed_share_amplification_reliability_label"
        class="mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
        :class="shareReliabilityClass(stats.observed_share_amplification_reliability)"
      >
        Reliability: {{ stats.observed_share_amplification_reliability_label }}
      </div>
      <div v-if="showBenchmark && benchmarkLabel('observed_share_amplification')" class="mt-2 text-xs font-medium text-gray-600">
        Benchmark (rolling 12 months): {{ benchmarkLabel('observed_share_amplification') }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EngagementKpiCards',
  props: {
    stats: { type: Object, required: true },
    tooltips: { type: Object, default: () => ({}) },
    showSessionAndPageViews: { type: Boolean, default: true },
    showBenchmark: { type: Boolean, default: false },
  },
  methods: {
    metricTooltip(key) {
      const metricTooltips = this.stats?.metric_tooltips
      if (metricTooltips && typeof metricTooltips === 'object' && metricTooltips[key]) return metricTooltips[key]
      return this.tooltips?.[key] || ''
    },
    metricLabel(key) {
      const labelled = this.stats?.[`${key}_label`]
      if (typeof labelled === 'string' && labelled.length > 0) return labelled
      if (key === 'graduate_amplification_index') {
        const raw = Number(this.stats?.graduate_amplification_index)
        return Number.isFinite(raw) ? `${raw.toFixed(2)}x` : 'N/A'
      }
      if (key === 'download_rate') {
        const raw = Number(this.stats?.download_rate)
        return Number.isFinite(raw) ? `${(raw * 100).toFixed(2)}%` : 'N/A'
      }
      const raw = Number(this.stats?.[key])
      return Number.isFinite(raw) ? raw.toFixed(2) : 'N/A'
    },
    shareReliabilityClass(level) {
      const key = String(level || '').toLowerCase()
      if (key === 'high') return 'bg-green-100 text-green-800'
      if (key === 'medium') return 'bg-amber-100 text-amber-800'
      return 'bg-gray-100 text-gray-700'
    },
    benchmarkLabel(metricKey) {
      const benchmark = this.stats?.benchmark_yearly
      const metrics = benchmark?.metrics
      const row = metrics?.[metricKey]
      const label = String(row?.label || '').trim()
      if (!label) return ''
      return label
    },
  },
}
</script>
